import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog, faTimes, faSave, faUndo, faPlus, faTrash, faInfoCircle, faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import axios from '../../config/axiosConfig';

/**
 * Login Detection Settings Modal
 * Allows users to configure login detection parameters without code changes
 */
const LoginDetectionSettings = ({ webTargetId, isDarkMode, onClose }) => {
  const [config, setConfig] = useState({
    enabled: true,
    urlPatterns: [],
    usernameFieldPatterns: [],
    passwordFieldPatterns: [],
    successStatusCodes: [],
    successKeywords: [],
    successHeaderNames: [],
    requirePasswordField: true,
    requireUrlMatch: false,
    minConfidence: 'medium',
    // Enable/disable flags for each detection component
    useUrlPatterns: true,
    useFieldPatterns: true,
    useStatusCodes: true,
    useKeywords: true,
    useHeaders: true
  });

  const [newPattern, setNewPattern] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newStatusCode, setNewStatusCode] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [newHeader, setNewHeader] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Default patterns
  const DEFAULT_CONFIG = {
    enabled: true,
    urlPatterns: [
      '/api/auth/login',
      '/api/login',
      '/login',
      '/signin',
      '/authenticate',
      '/wp-login.php',
      '/wp-admin',
      '/admin/login'
    ],
    usernameFieldPatterns: ['username', 'user', 'email', 'login', 'account'],
    passwordFieldPatterns: ['password', 'pass', 'pwd', 'secret'],
    successStatusCodes: [200, 201, 302, 303, 307, 308],
    successKeywords: ['success', 'authenticated', 'logged in', 'token', 'session', 'welcome'],
    successHeaderNames: ['set-cookie', 'authorization', 'x-auth-token', 'x-access-token'],
    requirePasswordField: true,
    requireUrlMatch: false,
    minConfidence: 'medium',
    useUrlPatterns: true,
    useFieldPatterns: true,
    useStatusCodes: true,
    useKeywords: true,
    useHeaders: true
  };

  useEffect(() => {
    loadSettings();
  }, [webTargetId]);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`/web-targets/${webTargetId}`);
      const savedConfig = response.data.loginDetectionConfig;

      if (savedConfig && Object.keys(savedConfig).length > 0) {
        setConfig(savedConfig);
      } else {
        setConfig(DEFAULT_CONFIG);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setConfig(DEFAULT_CONFIG);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`/web-targets/${webTargetId}`, {
        loginDetectionConfig: config
      });

      toast.success('✅ Login detection settings saved!');
      setHasChanges(false);
      if (onClose) onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setConfig(DEFAULT_CONFIG);
    setHasChanges(true);
  };

  const addPattern = (type) => {
    const value = {
      urlPatterns: newPattern,
      usernameFieldPatterns: newUsername,
      passwordFieldPatterns: newPassword,
      successStatusCodes: newStatusCode,
      successKeywords: newKeyword,
      successHeaderNames: newHeader
    }[type];

    const setter = {
      urlPatterns: setNewPattern,
      usernameFieldPatterns: setNewUsername,
      passwordFieldPatterns: setNewPassword,
      successStatusCodes: setNewStatusCode,
      successKeywords: setNewKeyword,
      successHeaderNames: setNewHeader
    }[type];

    if (!value) return;

    // For status codes, parse as number
    const processedValue = type === 'successStatusCodes' ? parseInt(value) : value;

    if (!config[type].includes(processedValue)) {
      setConfig(prev => ({
        ...prev,
        [type]: [...prev[type], processedValue]
      }));
      setHasChanges(true);
    }

    setter('');
  };

  const removeItem = (type, index) => {
    setConfig(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  const inputClass = `px-2 py-1 border rounded text-xs ${
    isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
  }`;

  const labelClass = `text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`;

  // Generate human-readable detection logic preview
  const getDetectionLogic = () => {
    if (!config.enabled) return 'Detection is disabled';

    const parts = [];

    // Step 1: HTTP Method filter (always applied)
    parts.push('POST, PUT, or PATCH request');

    // Step 2: URL or Password field requirement
    const urlCheck = config.useUrlPatterns && config.urlPatterns.length > 0
      ? `URL matches pattern (${config.urlPatterns.length} patterns)`
      : null;

    const passwordCheck = config.requirePasswordField
      ? 'password field in body'
      : null;

    if (config.requireUrlMatch && urlCheck) {
      parts.push(`AND ${urlCheck}`);
    } else if (urlCheck && passwordCheck) {
      parts.push(`AND (${urlCheck} OR ${passwordCheck})`);
    } else if (urlCheck) {
      parts.push(`AND ${urlCheck}`);
    } else if (passwordCheck) {
      parts.push(`AND ${passwordCheck}`);
    }

    // Step 3: Field patterns (if enabled)
    if (config.useFieldPatterns && config.usernameFieldPatterns.length > 0 && config.passwordFieldPatterns.length > 0) {
      parts.push(`AND username + password fields detected`);
    }

    // Step 4: Success indicators
    const successChecks = [];
    if (config.useStatusCodes && config.successStatusCodes.length > 0) {
      successChecks.push(`status ${config.successStatusCodes.join(', ')}`);
    }
    if (config.useKeywords && config.successKeywords.length > 0) {
      successChecks.push(`keywords found`);
    }
    if (config.useHeaders && config.successHeaderNames.length > 0) {
      successChecks.push(`headers present`);
    }

    if (successChecks.length > 0) {
      parts.push(`AND successful response (${successChecks.join(' OR ')})`);
    }

    return parts.join(' ');
  };

  const isUsingDefaults = () => {
    return JSON.stringify(config) === JSON.stringify(DEFAULT_CONFIG);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className={`w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-lg shadow-xl ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`px-4 py-3 border-b flex items-center justify-between ${
          isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faCog} className="text-blue-500" />
            <h3 className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              Login Detection Settings
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHelp(true)}
              className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                isDarkMode ? 'bg-blue-700 text-blue-200 hover:bg-blue-600' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
              title="How does login detection work?"
            >
              <FontAwesomeIcon icon={faQuestionCircle} /> How it works
            </button>
            <button
              onClick={onClose}
              className={`p-1 rounded hover:bg-gray-700 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)] space-y-4">
          {/* Enable/Disable */}
          <div className={`p-3 rounded-lg border ${
            isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'
          }`}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={e => {
                  setConfig(prev => ({ ...prev, enabled: e.target.checked }));
                  setHasChanges(true);
                }}
                className="rounded"
              />
              <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                Enable Auto-Detection of Login Requests
              </span>
            </label>
            <p className={`text-[10px] mt-1 ml-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              Automatically detect login attempts in proxy traffic and create auth profiles
            </p>
            <div className={`text-[10px] mt-2 ml-6 flex items-center gap-1 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>
              <FontAwesomeIcon icon={faInfoCircle} className="text-[9px]" />
              Only POST/PUT/PATCH requests are analyzed (GET requests are always ignored)
            </div>
            {!isUsingDefaults() && (
              <div className={`text-[9px] mt-1 ml-6 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                ⚠️ Custom configuration active (not using defaults)
              </div>
            )}
          </div>

          {config.enabled && (
            <>
              {/* URL Patterns */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={labelClass}>URL Patterns (Regex)</label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.useUrlPatterns}
                      onChange={e => {
                        setConfig(prev => ({ ...prev, useUrlPatterns: e.target.checked }));
                        setHasChanges(true);
                      }}
                      className="rounded w-3 h-3"
                    />
                    <span className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                      Use in detection
                    </span>
                  </label>
                </div>
                <div className={`mt-1 p-2 rounded border ${
                  config.useUrlPatterns
                    ? (isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-300 bg-gray-50')
                    : (isDarkMode ? 'border-gray-800 bg-gray-900/50 opacity-50' : 'border-gray-200 bg-gray-100 opacity-50')
                }`}>
                  <div className="flex gap-1 mb-2">
                    <input
                      value={newPattern}
                      onChange={e => setNewPattern(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && config.useUrlPatterns && addPattern('urlPatterns')}
                      placeholder="/login, /signin, /wp-login.php"
                      disabled={!config.useUrlPatterns}
                      className={`flex-1 ${inputClass}`}
                    />
                    <button
                      onClick={() => addPattern('urlPatterns')}
                      disabled={!config.useUrlPatterns}
                      className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {config.urlPatterns.map((pattern, idx) => (
                      <span key={idx} className={`px-2 py-0.5 rounded text-[10px] font-mono flex items-center gap-1 ${
                        isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'
                      }`}>
                        {pattern}
                        <button
                          onClick={() => removeItem('urlPatterns', idx)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-[8px]" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Field Patterns (Username + Password) */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={labelClass}>Login Field Detection</label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.useFieldPatterns}
                      onChange={e => {
                        setConfig(prev => ({ ...prev, useFieldPatterns: e.target.checked }));
                        setHasChanges(true);
                      }}
                      className="rounded w-3 h-3"
                    />
                    <span className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                      Use in detection
                    </span>
                  </label>
                </div>

                {/* Username Field Patterns */}
                <div className={`text-[10px] font-medium mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Username Field Names
                </div>
                <div className={`mt-1 p-2 rounded border ${
                  config.useFieldPatterns
                    ? (isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-300 bg-gray-50')
                    : (isDarkMode ? 'border-gray-800 bg-gray-900/50 opacity-50' : 'border-gray-200 bg-gray-100 opacity-50')
                }`}>
                  <div className="flex gap-1 mb-2">
                    <input
                      value={newUsername}
                      onChange={e => setNewUsername(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && config.useFieldPatterns && addPattern('usernameFieldPatterns')}
                      placeholder="username, user, email, login"
                      disabled={!config.useFieldPatterns}
                      className={`flex-1 ${inputClass}`}
                    />
                    <button
                      onClick={() => addPattern('usernameFieldPatterns')}
                      disabled={!config.useFieldPatterns}
                      className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {config.usernameFieldPatterns.map((pattern, idx) => (
                      <span key={idx} className={`px-2 py-0.5 rounded text-[10px] font-mono flex items-center gap-1 ${
                        isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'
                      }`}>
                        {pattern}
                        <button
                          onClick={() => removeItem('usernameFieldPatterns', idx)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-[8px]" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Password Field Patterns */}
                <div className={`text-[10px] font-medium mt-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Password Field Names
                </div>
                <div className={`mt-1 p-2 rounded border ${
                  config.useFieldPatterns
                    ? (isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-300 bg-gray-50')
                    : (isDarkMode ? 'border-gray-800 bg-gray-900/50 opacity-50' : 'border-gray-200 bg-gray-100 opacity-50')
                }`}>
                  <div className="flex gap-1 mb-2">
                    <input
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && config.useFieldPatterns && addPattern('passwordFieldPatterns')}
                      placeholder="password, pass, pwd, secret"
                      disabled={!config.useFieldPatterns}
                      className={`flex-1 ${inputClass}`}
                    />
                    <button
                      onClick={() => addPattern('passwordFieldPatterns')}
                      disabled={!config.useFieldPatterns}
                      className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {config.passwordFieldPatterns.map((pattern, idx) => (
                      <span key={idx} className={`px-2 py-0.5 rounded text-[10px] font-mono flex items-center gap-1 ${
                        isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'
                      }`}>
                        {pattern}
                        <button
                          onClick={() => removeItem('passwordFieldPatterns', idx)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-[8px]" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Success Status Codes */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={labelClass}>Success Status Codes</label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.useStatusCodes}
                      onChange={e => {
                        setConfig(prev => ({ ...prev, useStatusCodes: e.target.checked }));
                        setHasChanges(true);
                      }}
                      className="rounded w-3 h-3"
                    />
                    <span className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                      Use in detection
                    </span>
                  </label>
                </div>
                <div className={`mt-1 p-2 rounded border ${
                  config.useStatusCodes
                    ? (isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-300 bg-gray-50')
                    : (isDarkMode ? 'border-gray-800 bg-gray-900/50 opacity-50' : 'border-gray-200 bg-gray-100 opacity-50')
                }`}>
                  <div className="flex gap-1 mb-2">
                    <input
                      type="number"
                      value={newStatusCode}
                      onChange={e => setNewStatusCode(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && config.useStatusCodes && addPattern('successStatusCodes')}
                      placeholder="200, 201, 302"
                      disabled={!config.useStatusCodes}
                      className={`flex-1 ${inputClass}`}
                    />
                    <button
                      onClick={() => addPattern('successStatusCodes')}
                      disabled={!config.useStatusCodes}
                      className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {config.successStatusCodes.map((code, idx) => (
                      <span key={idx} className={`px-2 py-0.5 rounded text-[10px] font-mono flex items-center gap-1 ${
                        isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'
                      }`}>
                        {code}
                        <button
                          onClick={() => removeItem('successStatusCodes', idx)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-[8px]" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Success Keywords */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={labelClass}>Success Keywords (in response body)</label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.useKeywords}
                      onChange={e => {
                        setConfig(prev => ({ ...prev, useKeywords: e.target.checked }));
                        setHasChanges(true);
                      }}
                      className="rounded w-3 h-3"
                    />
                    <span className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                      Use in detection
                    </span>
                  </label>
                </div>
                <div className={`mt-1 p-2 rounded border ${
                  config.useKeywords
                    ? (isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-300 bg-gray-50')
                    : (isDarkMode ? 'border-gray-800 bg-gray-900/50 opacity-50' : 'border-gray-200 bg-gray-100 opacity-50')
                }`}>
                  <div className="flex gap-1 mb-2">
                    <input
                      value={newKeyword}
                      onChange={e => setNewKeyword(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && config.useKeywords && addPattern('successKeywords')}
                      placeholder="success, authenticated, token"
                      disabled={!config.useKeywords}
                      className={`flex-1 ${inputClass}`}
                    />
                    <button
                      onClick={() => addPattern('successKeywords')}
                      disabled={!config.useKeywords}
                      className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {config.successKeywords.map((keyword, idx) => (
                      <span key={idx} className={`px-2 py-0.5 rounded text-[10px] font-mono flex items-center gap-1 ${
                        isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'
                      }`}>
                        {keyword}
                        <button
                          onClick={() => removeItem('successKeywords', idx)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-[8px]" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Success Header Names */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={labelClass}>Success Header Names</label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.useHeaders}
                      onChange={e => {
                        setConfig(prev => ({ ...prev, useHeaders: e.target.checked }));
                        setHasChanges(true);
                      }}
                      className="rounded w-3 h-3"
                    />
                    <span className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                      Use in detection
                    </span>
                  </label>
                </div>
                <div className={`mt-1 p-2 rounded border ${
                  config.useHeaders
                    ? (isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-300 bg-gray-50')
                    : (isDarkMode ? 'border-gray-800 bg-gray-900/50 opacity-50' : 'border-gray-200 bg-gray-100 opacity-50')
                }`}>
                  <div className="flex gap-1 mb-2">
                    <input
                      value={newHeader}
                      onChange={e => setNewHeader(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && config.useHeaders && addPattern('successHeaderNames')}
                      placeholder="set-cookie, authorization"
                      disabled={!config.useHeaders}
                      className={`flex-1 ${inputClass}`}
                    />
                    <button
                      onClick={() => addPattern('successHeaderNames')}
                      disabled={!config.useHeaders}
                      className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {config.successHeaderNames.map((header, idx) => (
                      <span key={idx} className={`px-2 py-0.5 rounded text-[10px] font-mono flex items-center gap-1 ${
                        isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'
                      }`}>
                        {header}
                        <button
                          onClick={() => removeItem('successHeaderNames', idx)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-[8px]" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Detection Heuristics */}
              <div className={`p-3 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-300 bg-gray-50'}`}>
                <div className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Detection Heuristics
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.requirePasswordField}
                      onChange={e => {
                        setConfig(prev => ({ ...prev, requirePasswordField: e.target.checked }));
                        setHasChanges(true);
                      }}
                      className="rounded"
                    />
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Require password field in request body
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.requireUrlMatch}
                      onChange={e => {
                        setConfig(prev => ({ ...prev, requireUrlMatch: e.target.checked }));
                        setHasChanges(true);
                      }}
                      className="rounded"
                    />
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Require URL pattern match
                    </span>
                  </label>
                </div>
              </div>

              {/* Detection Logic Preview */}
              <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-purple-900/20 border-purple-800' : 'bg-purple-50 border-purple-200'}`}>
                <div className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-900'}`}>
                  🔬 Derived Detection Logic
                </div>
                <div className={`text-xs font-mono p-2 rounded ${isDarkMode ? 'bg-gray-900 text-cyan-300' : 'bg-white text-cyan-800'}`}>
                  {getDetectionLogic()}
                </div>
                <div className={`text-[9px] mt-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                  This is the actual detection logic that will be applied based on your settings above.
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className={`px-4 py-3 border-t flex items-center justify-between ${
          isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'
        }`}>
          <button
            onClick={handleReset}
            className={`px-3 py-1.5 rounded text-xs flex items-center gap-1 ${
              isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <FontAwesomeIcon icon={faUndo} /> Reset to Defaults
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className={`px-3 py-1.5 rounded text-xs ${
                isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading || !hasChanges}
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
            >
              <FontAwesomeIcon icon={faSave} /> Save Settings
            </button>
          </div>
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowHelp(false)}>
          <div
            className={`w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-lg shadow-xl ${
              isDarkMode ? 'bg-gray-800' : 'bg-white'
            }`}
            onClick={e => e.stopPropagation()}
          >
            {/* Help Header */}
            <div className={`px-4 py-3 border-b flex items-center justify-between ${
              isDarkMode ? 'border-gray-700 bg-blue-900/30' : 'border-gray-200 bg-blue-50'
            }`}>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faQuestionCircle} className="text-blue-500 text-lg" />
                <h3 className={`font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                  How Login Detection Works
                </h3>
              </div>
              <button
                onClick={() => setShowHelp(false)}
                className={`p-1 rounded hover:bg-gray-700 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Help Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)] space-y-4">
              {/* Overview */}
              <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                  📋 Overview
                </h4>
                <p className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-800'}`}>
                  Login detection automatically identifies login requests in proxy traffic and creates authentication profiles.
                  It uses multiple detection methods (URL patterns, request body analysis, response indicators) to find login attempts with high accuracy.
                </p>
              </div>

              {/* How It Works */}
              <div>
                <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                  🔍 Detection Process
                </h4>
                <div className={`p-3 rounded-lg space-y-2 text-xs ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                  <div className="flex items-start gap-2">
                    <span className={`font-bold min-w-[60px] ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>Step 1:</span>
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                      Filter by HTTP method (POST, PUT, PATCH) - logins are rarely GET requests
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className={`font-bold min-w-[60px] ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>Step 2:</span>
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                      Check if URL matches login patterns (e.g., /login, /signin, /wp-login.php)
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className={`font-bold min-w-[60px] ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>Step 3:</span>
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                      Analyze request body for login fields (username + password) - even if URL doesn't match
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className={`font-bold min-w-[60px] ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>Step 4:</span>
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                      Verify response indicates success (status codes, keywords, headers like Set-Cookie)
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className={`font-bold min-w-[60px] ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>Step 5:</span>
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                      Extract auth artifacts (cookies, JWT, Bearer tokens) from response
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className={`font-bold min-w-[60px] ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>Step 6:</span>
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                      Create AuthProfile with credentials, auth type, and detection metadata
                    </span>
                  </div>
                </div>
              </div>

              {/* Settings Explained */}
              <div>
                <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                  ⚙️ Settings Explained
                </h4>
                <div className="space-y-2">
                  {/* URL Patterns */}
                  <div className={`p-2 rounded border ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                    <div className={`text-xs font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                      URL Patterns (Regex)
                    </div>
                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Regular expressions tested against the request URL. Add patterns like <code className="font-mono">/login</code>, <code className="font-mono">/wp-admin</code>, <code className="font-mono">/api/auth</code>.
                      More specific patterns reduce false positives.
                    </p>
                  </div>

                  {/* Field Names */}
                  <div className={`p-2 rounded border ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                    <div className={`text-xs font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                      Username/Password Field Names
                    </div>
                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Field names to look for in POST request bodies. Common: <code className="font-mono">username</code>, <code className="font-mono">email</code>, <code className="font-mono">password</code>.
                      Add custom names if your app uses different field names (e.g., <code className="font-mono">user_name</code>, <code className="font-mono">passwd</code>).
                    </p>
                  </div>

                  {/* Status Codes */}
                  <div className={`p-2 rounded border ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                    <div className={`text-xs font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                      Success Status Codes
                    </div>
                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      HTTP status codes that indicate successful login. <code className="font-mono">200</code> = OK, <code className="font-mono">302</code> = Redirect (common for session-based logins).
                      WordPress often uses 302 redirects on success.
                    </p>
                  </div>

                  {/* Keywords */}
                  <div className={`p-2 rounded border ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                    <div className={`text-xs font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                      Success Keywords
                    </div>
                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Words found in response body that indicate successful authentication. Examples: <code className="font-mono">success</code>, <code className="font-mono">authenticated</code>, <code className="font-mono">token</code>.
                      JSON responses often include <code className="font-mono">{`{"success": true}`}</code>.
                    </p>
                  </div>

                  {/* Headers */}
                  <div className={`p-2 rounded border ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                    <div className={`text-xs font-medium ${isDarkMode ? 'text-pink-400' : 'text-pink-700'}`}>
                      Success Header Names
                    </div>
                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      HTTP headers present in successful login responses. <code className="font-mono">set-cookie</code> is most common (session cookies), <code className="font-mono">authorization</code> for token-based auth.
                      Modern SPAs often return JWT in <code className="font-mono">x-auth-token</code> header.
                    </p>
                  </div>

                  {/* Heuristics */}
                  <div className={`p-2 rounded border ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                    <div className={`text-xs font-medium ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                      Detection Heuristics
                    </div>
                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <strong>"Require password field"</strong>: Only detect logins if password field is present in request. Reduces false positives from non-login POST requests.<br/>
                      <strong>"Require URL match"</strong>: Only detect logins if URL matches a pattern. Strict mode - may miss logins with non-standard URLs.
                    </p>
                  </div>
                </div>
              </div>

              {/* Use Cases */}
              <div>
                <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                  💡 Common Use Cases
                </h4>
                <div className="space-y-2">
                  <div className={`p-2 rounded border ${isDarkMode ? 'border-yellow-800 bg-yellow-900/20' : 'border-yellow-200 bg-yellow-50'}`}>
                    <div className={`text-xs font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-800'}`}>
                      ❌ WordPress detecting backend navigation instead of login
                    </div>
                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-yellow-500' : 'text-yellow-700'}`}>
                      <strong>Solution</strong>: Remove <code className="font-mono">/wp-admin</code> from URL patterns. Only keep <code className="font-mono">/wp-login\.php</code>.
                      Enable "Require password field" to ensure it's actually a login POST.
                    </p>
                  </div>

                  <div className={`p-2 rounded border ${isDarkMode ? 'border-green-800 bg-green-900/20' : 'border-green-200 bg-green-50'}`}>
                    <div className={`text-xs font-medium ${isDarkMode ? 'text-green-400' : 'text-green-800'}`}>
                      ✅ Custom CMS not being detected
                    </div>
                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-green-500' : 'text-green-700'}`}>
                      <strong>Solution</strong>: Add your custom login URL pattern (e.g., <code className="font-mono">/myapp/auth/signin</code>).
                      Add custom field names if different from standard (e.g., <code className="font-mono">email_address</code>, <code className="font-mono">user_password</code>).
                    </p>
                  </div>

                  <div className={`p-2 rounded border ${isDarkMode ? 'border-blue-800 bg-blue-900/20' : 'border-blue-200 bg-blue-50'}`}>
                    <div className={`text-xs font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-800'}`}>
                      🔧 Too many false positives
                    </div>
                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-blue-500' : 'text-blue-700'}`}>
                      <strong>Solution</strong>: Enable both "Require password field" AND "Require URL match". This ensures only requests that match BOTH criteria are detected.
                      More restrictive = fewer false positives but may miss edge cases.
                    </p>
                  </div>

                  <div className={`p-2 rounded border ${isDarkMode ? 'border-purple-800 bg-purple-900/20' : 'border-purple-200 bg-purple-50'}`}>
                    <div className={`text-xs font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-800'}`}>
                      🎯 API-only application (JSON login)
                    </div>
                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-purple-500' : 'text-purple-700'}`}>
                      <strong>Solution</strong>: Add success keywords like <code className="font-mono">access_token</code>, <code className="font-mono">refresh_token</code>, <code className="font-mono">jwt</code>.
                      Add custom header names if API uses non-standard headers (e.g., <code className="font-mono">x-api-key</code>).
                    </p>
                  </div>
                </div>
              </div>

              {/* Best Practices */}
              <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'}`}>
                <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-green-300' : 'text-green-900'}`}>
                  ✅ Best Practices
                </h4>
                <ul className={`text-xs space-y-1 list-disc list-inside ${isDarkMode ? 'text-green-400' : 'text-green-800'}`}>
                  <li>Start with defaults, then tune based on your application's behavior</li>
                  <li>Use specific URL patterns (e.g., <code className="font-mono">/api/v1/auth/login</code>) rather than broad ones</li>
                  <li>Enable "Require password field" to reduce false positives from non-login POSTs</li>
                  <li>Check the "Auto-Creation Details" on created profiles to see which rules triggered</li>
                  <li>Test detection by logging out and back in while proxy is running</li>
                  <li>Different settings per web target - each site can have its own configuration</li>
                </ul>
              </div>

              {/* Troubleshooting */}
              <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
                <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-900'}`}>
                  🔧 Troubleshooting
                </h4>
                <div className={`text-xs space-y-2 ${isDarkMode ? 'text-red-400' : 'text-red-800'}`}>
                  <div>
                    <strong>Login not detected?</strong>
                    <ul className="list-disc list-inside ml-2 mt-0.5">
                      <li>Check proxy is running and traffic is being captured</li>
                      <li>Verify login is POST/PUT/PATCH (not GET)</li>
                      <li>Check if URL matches any patterns OR password field is present</li>
                      <li>Verify response status is in success codes list</li>
                      <li>Disable "Require URL match" to rely on password heuristic only</li>
                    </ul>
                  </div>
                  <div>
                    <strong>Wrong profile created?</strong>
                    <ul className="list-disc list-inside ml-2 mt-0.5">
                      <li>Delete the incorrect profile</li>
                      <li>Adjust URL patterns to be more specific</li>
                      <li>Enable stricter heuristics (require both URL match + password field)</li>
                      <li>Log out, update settings, log in again to test</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Help Footer */}
            <div className={`px-4 py-3 border-t flex justify-end ${
              isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'
            }`}>
              <button
                onClick={() => setShowHelp(false)}
                className="px-4 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginDetectionSettings;
